import DatawrapperChart from 'components/datawrapper'
import { ReactNode } from 'react'

type QuestionCommon = {
  title: string
  instructions?: ReactNode
  description?: ReactNode
}

export type QuestionSelect = QuestionCommon & {
  options: string[]
  correctAnswer: string
  condensed?: boolean
}

export type Questionslider = QuestionCommon & {
  correctAnswer: number
}

export type Question = QuestionSelect | Questionslider

export const questions: Question[] = [
  {
    title:
      'Как вы думаете, кто чаще всего совершает насилие в отношении женщины?',
    options: ['Знакомый человек', 'Незнакомый человек'],
    correctAnswer: 'Знакомый человек',
    description: (
      <>
        <p>
          Чаще всего насилие в отношении женщин совершают знакомые им люди. Это
          может быть партнер, член семьи, коллега или друг. Такая тенденция
          подтверждается, например, совместным{' '}
          <a
            href="https://www.unwomen.org/en/digital-library/publications/2024/11/femicides-in-2023-global-estimates-of-intimate-partner-family-member-femicides"
            target="_blank"
            rel="noreferrer"
          >
            исследованием
          </a>{' '}
          структуры "ООН-женщины" и Управления ООН по наркотикам и преступности
          о фемициде в мире.
        </p>
        <p>
          Об этом же рассказала почти тысяча жителей Центральной Азии проекту
          "Голоса смелых". Среди 933 участниц и участников опроса лишь 72
          человека сообщили о насилии со стороны незнакомых людей. Во всех
          остальных случаях это были родственники, партнеры и коллеги.
        </p>
      </>
    ),
  },
  {
    title: 'Кому чаще всего рассказывали о пережитом насилии?',
    instructions: 'Выберите самый популярный ответ:',
    options: [
      'Близкой подруге',
      'Родственнику',
      'Психологу',
      'Правоохранительным органам',
    ],
    correctAnswer: 'Близкой подруге',
    description: (
      <p>
        Исходя из результатов опроса, большинство людей, переживших насилие,
        склонны доверять своим близким, таким как подруга (43%) или родственник
        (30%), а не обращаться к профессионалам (16%) или властям (5%).
      </p>
    ),
  },
  {
    title:
      'Оцените, насколько верно следующее утверждение: "С насилием сталкиваются только взрослые женщины".',
    options: ['Миф', 'Правда'],
    correctAnswer: 'Миф',
    description: (
      <p>
        Это утверждение – миф. Насилие может затронуть людей разного возраста и
        пола. Например, согласно нашему опросу, 33% респондентов отметили, что
        впервые столкнулись с насилием в детстве (до 12 лет), 21% – в
        подростковом возрасте (13-17 лет), а 11% – в юности (18-24 года).
      </p>
    ),
  },
  {
    title:
      'Какой процент опрошенных нами, переживших насилие, боится, что оно может повториться в будущем?',
    instructions: (
      <>
        Ползунок от <strong>0</strong> до <strong>100%</strong>
      </>
    ),
    correctAnswer: 56,
    description: (
      <p>
        По данным нашего опроса, больше половины столкнувшихся с насилием боятся
        повторного насилия.
      </p>
    ),
  },
  {
    title: 'Как часто пострадавшие сталкиваются с насилием?',
    options: ['Каждый день', 'Периодически', 'Единожды', 'Крайне редко'],
    correctAnswer: 'Периодически',
    description: (
      <>
        <p>
          К сожалению, если насилие случилось единожды, с большой долей
          вероятности оно может снова повторится. Об этом говорят независимые
          исследования, в том числе{' '}
          <a
            href="https://www.sciencedirect.com/science/article/pii/S2665910724000057"
            target="_blank"
            rel="noreferrer"
          >
            работа
          </a>{' '}
          ученых из Карагандинского медицинского университета. По их данным, 78%
          женщин, обратившихся в судмедэкспертизу, сообщили о повторных фактах
          физического насилия.
        </p>
        <p>
          Наш опрос подтверждает эту тенденцию: четверть ответивших поделились,
          что подвергаются насилию несколько раз в год, а каждая восьмая женщина
          – каждый день.
        </p>
      </>
    ),
  },
  {
    title: 'Какой вид насилия самый распространенный?',
    options: [
      'Физическое насилие',
      'Экономическое насилие',
      'Сексуализированное насилие',
      'Психологическое насилие',
    ],
    correctAnswer: 'Психологическое насилие',
    description: (
      <>
        <p>
          Многие случаи насилия не попадают в статистику, из-за этого очень
          сложно выделить какой-то определенный вид. К тому же разные виды
          насилия часто сопряжены друг с другом. По результатам нашего опроса,
          пережившие насилие женщины и мужчины чаще всего подвергались
          психологическому насилию. Однако физическое и сексуализированное
          насилие также случались с примерно одинаковой частотой.
        </p>

        <DatawrapperChart
          title="Чаще всего респонденты сталкиваются с психологическим насилием"
          ariaLabel="Interactive line chart"
          src="https://datawrapper.dwcdn.net/yYhTE/6/"
          height="245"
          nomargin
        />
      </>
    ),
  },
  {
    title:
      'Что способствует распространению гендерного насилия в странах Центральной Азии по мнению участников опроса?',
    instructions: 'Выберите самый популярный ответ:',
    options: [
      'Экономическая зависимость',
      'Традиционные роли в семье',
      'Отсутствие наказаний для виновных',
      'Страх перед обществом',
      'Недостаток поддержки для людей, переживших насилие',
      'Неосведомленность о правах и ресурсах',
    ],
    condensed: true,
    correctAnswer: 'Традиционные роли в семье',
    description: (
      <>
        <p>
          Традиционные роли в семье – главная причина распространения насилия,
          по мнению респондентов из Центральной Азии. Часто также назывался и
          другой фактор – отсутствие наказания для виновных.
        </p>

        <DatawrapperChart
          title="Большинство респондентов считает, что традиционные роли способствуют гендерному насилию"
          ariaLabel="Interactive line chart"
          src="https://datawrapper.dwcdn.net/jPay8/6/"
          height="343"
          nomargin
        />
      </>
    ),
  },
]
